import { MediaPlacement, MediaType, ProjectStatus } from "@prisma/client";
import { z } from "zod";

export const projectMediaSchema = z.object({
  id: z.string().optional(),
  url: z.string().min(1, "Media URL is required")
    .refine(url => !url.startsWith('blob:'), "Please wait for media upload to complete")
    .refine(url => url.startsWith('https://') || url.startsWith('http://'), "Media URL must be a valid HTTP(S) URL"),
  placement: z.nativeEnum(MediaPlacement, {
    error: "Invalid media placement type"
  }).default(MediaPlacement.PROJECT_MEDIA),
  type: z.nativeEnum(MediaType, {
    error: "Invalid media type"
  }).default(MediaType.IMAGE),
  mimeType: z.string().min(1, "Media type is required").default('image/jpeg'),
  size: z.number().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  altText: z.string().optional(),
  dpi: z.number().optional(),
  caption: z.string().optional(),
  durationSeconds: z.number().optional(),
  originalName: z.string().optional(),
  metadata: z.object({
    ownerId: z.string().min(1, "Media owner ID is required"),
    width: z.number().optional(),
    height: z.number().optional(),
    alt: z.string().optional(),
    dimensions: z.object({
      width: z.number(),
      height: z.number(),
      aspectRatio: z.number()
    }).optional(),
    originalName: z.string().nullable().optional(),
    mimeType: z.string().nullable().optional(),
    durationSeconds: z.number().optional()
  }),
  createdAt: z.coerce.date().default(() => new Date()),
  updatedAt: z.coerce.date().default(() => new Date())
})

export const baseProjectSchema = z.object({
  id: z.string().optional(),
  title: z.string().min(1, 'Title is required').max(100, 'Title must be less than 100 characters'),
  description: z.string().optional(),
  slug: z.string().optional(),
  status: z.nativeEnum(ProjectStatus, {
    error: 'Please select a valid status'
  }),
  categoryId: z.string({
    error: (issue) => issue.input === undefined ? 'Please select a category' : 'Please select a valid category'
  }).min(1, 'Please select a category'),
  userId: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  publishedAt: z.string().datetime().nullable().optional(),
  isDeleted: z.boolean().optional(),
  isFeatured: z.boolean().optional(),
  isSponsored: z.boolean().optional(),
  isPromoted: z.boolean().optional(),
  viewCount: z.number().default(0),
  metaTitle: z.string().nullable().optional(),
  metaDescription: z.string().nullable().optional(),
  keywords: z.array(z.string()).optional(),
  vehicleId: z.string().optional(),
  media: z.array(projectMediaSchema)
    .max(10, "Maximum of 10 media items allowed")
    .refine(
      (media) => !media?.some(item => item.url?.startsWith('blob:')),
      "Please wait for all media uploads to complete"
    )
    .optional()
    .default([]),
  mediaIds: z.array(z.string()).optional()
})

export const projectSchema = baseProjectSchema.refine((data) => {
  if (data.status === ProjectStatus.PUBLISHED) {
    const hasDescription = !!data.description && data.description.length > 0;
    const hasMedia = !!data.media && data.media.length > 0;
    return hasDescription && hasMedia;
  }
  return true;
}, {
  message: "Published projects require a description and at least one media item",
  path: ["description"]
})

export const projectUpdateSchema = z.object({
  ...baseProjectSchema.omit({ id: true }).shape
}).partial()

export type ProjectInputData = z.infer<typeof projectSchema>
export type ProjectUpdateData = z.infer<typeof projectUpdateSchema> & {
  mediaIds?: string[]
}

// Define required fields for publishing
const REQUIRED_FIELDS_FOR_PUBLISHING = {
  DRAFT: [] as (keyof ProjectInputData)[],
  PUBLISHED: ['title', 'description', 'categoryId', 'media'] as (keyof ProjectInputData)[],
  DELETED: [] as (keyof ProjectInputData)[]
};

// Define status transition conditions
const STATUS_TRANSITIONS: Record<ProjectStatus, {
  allowedTransitions: ProjectStatus[];
  conditions: (data: ProjectInputData) => { valid: boolean; message?: string };
}> = {
  DRAFT: {
    allowedTransitions: [ProjectStatus.PUBLISHED, ProjectStatus.DELETED],
    conditions: (data: ProjectInputData) => {
      if (!data) return { valid: false, message: 'No data provided' };
      return { valid: true };
    }
  },
  PUBLISHED: {
    allowedTransitions: [ProjectStatus.DRAFT, ProjectStatus.DELETED],
    conditions: (data: ProjectInputData) => {
      if (!data) return { valid: false, message: 'No data provided' };
      return { valid: true };
    }
  },
  DELETED: {
    allowedTransitions: [ProjectStatus.DRAFT],
    conditions: (data: ProjectInputData) => {
      if (!data) return { valid: false, message: 'No data provided' };
      return { valid: true };
    }
  }
};

// Export helper functions
export function getValidStatusTransitions(currentStatus: ProjectStatus): ProjectStatus[] {
  return STATUS_TRANSITIONS[currentStatus]?.allowedTransitions || [];
}

export function validateStatusTransition(
  currentStatus: ProjectStatus,
  newStatus: ProjectStatus,
  data: ProjectInputData
): { valid: boolean; message?: string } {
  // Check if transition is allowed
  if (!STATUS_TRANSITIONS[currentStatus]?.allowedTransitions.includes(newStatus)) {
    return {
      valid: false,
      message: `Cannot transition from ${currentStatus} to ${newStatus}`
    };
  }

  // Check if required fields are present for the new status
  const requiredFields = REQUIRED_FIELDS_FOR_PUBLISHING[newStatus];
  const missingFields = requiredFields.filter(field => !data[field]);

  if (missingFields.length > 0) {
    return {
      valid: false,
      message: `Missing required fields for ${newStatus}: ${missingFields.join(', ')}`
    };
  }

  // Check additional conditions
  return STATUS_TRANSITIONS[currentStatus].conditions(data);
}

// Helper to check if a project can be published
export function canPublishProject(data: Partial<ProjectInputData>): {
  valid: boolean;
  message?: string;
} {
  // Convert partial data to full data with defaults
  const fullData: ProjectInputData = {
    title: data.title || '',
    description: data.description || '',
    status: data.status || ProjectStatus.DRAFT,
    categoryId: data.categoryId || '',
    media: data.media || [],
    keywords: data.keywords || [],
    startDate: data.startDate || undefined,
    endDate: data.endDate || undefined,
    publishedAt: data.publishedAt || undefined,
    metaTitle: data.metaTitle || undefined,
    metaDescription: data.metaDescription || undefined,
    isDeleted: data.isDeleted || false,
    isFeatured: data.isFeatured || false,
    isSponsored: data.isSponsored || false,
    isPromoted: data.isPromoted || false,
    viewCount: data.viewCount || 0,
    vehicleId: data.vehicleId || undefined,
  };

  return STATUS_TRANSITIONS['DRAFT'].conditions(fullData);
}

